/**
 * Essential WCAG 2.1 AA Accessibility utilities for VegaDigital marketing website
 */

import { useEffect, useState } from 'react';

/**
 * Focus management utilities
 */
export class FocusManager {
  static announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.setAttribute('class', 'sr-only');
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  }

  static trapFocus(element: HTMLElement): () => void {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable?.focus();
          e.preventDefault();
        }
      }
    };

    element.addEventListener('keydown', handleTabKey);
    return () => element.removeEventListener('keydown', handleTabKey);
  }
}

/**
 * Color contrast utilities for WCAG AA compliance
 */
export class ColorContrast {
  private static getLuminance(r: number, g: number, b: number): number {
    const [rs, gs, bs] = [r, g, b].map((c) => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  }

  private static hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  static getContrastRatio(color1: string, color2: string): number {
    const rgb1 = this.hexToRgb(color1);
    const rgb2 = this.hexToRgb(color2);

    if (!rgb1 || !rgb2) return 0;

    const lum1 = this.getLuminance(rgb1.r, rgb1.g, rgb1.b);
    const lum2 = this.getLuminance(rgb2.r, rgb2.g, rgb2.b);

    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);

    return (brightest + 0.05) / (darkest + 0.05);
  }

  static meetsWCAGAA(foreground: string, background: string, largeText = false): boolean {
    const ratio = this.getContrastRatio(foreground, background);
    return largeText ? ratio >= 3 : ratio >= 4.5;
  }
}

/**
 * Keyboard navigation hook
 */
export const useKeyboardNavigation = (onEscape?: () => void) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onEscape) {
        onEscape();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onEscape]);
};

/**
 * ARIA live region hook
 */
export const useAriaLiveRegion = () => {
  const [announcement, setAnnouncement] = useState<string>('');

  const announce = (message: string) => {
    setAnnouncement(message);
    setTimeout(() => setAnnouncement(''), 1000);
  };

  return { announce, announcement };
};

/**
 * Reduced motion preference detection
 */
export const useReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

/**
 * High contrast mode detection
 */
export const useHighContrastMode = (): boolean => {
  const [isHighContrast, setIsHighContrast] = useState(false);

  useEffect(() => {
    const checkHighContrast = () => {
      const highContrastMedia = window.matchMedia('(prefers-contrast: high)');
      setIsHighContrast(highContrastMedia.matches);
    };

    checkHighContrast();

    const highContrastMedia = window.matchMedia('(prefers-contrast: high)');
    const handleChange = () => checkHighContrast();
    
    highContrastMedia.addEventListener('change', handleChange);
    return () => highContrastMedia.removeEventListener('change', handleChange);
  }, []);

  return isHighContrast;
};

/**
 * Focus visible management
 */
export const useFocusVisible = (): boolean => {
  const [isKeyboardUser, setIsKeyboardUser] = useState(false);

  useEffect(() => {
    const handleKeyDown = () => setIsKeyboardUser(true);
    const handleMouseDown = () => setIsKeyboardUser(false);

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  return isKeyboardUser;
};

/**
 * Form accessibility utilities
 */
export const FormAccessibility = {
  generateFieldId: (fieldName: string): string => {
    return `field-${fieldName}-${Math.random().toString(36).substr(2, 9)}`;
  },

  validateFieldAccessibility: (fieldElement: HTMLElement): string[] => {
    const issues: string[] = [];

    const hasLabel = fieldElement.getAttribute('aria-labelledby') || 
                    fieldElement.getAttribute('aria-label') ||
                    document.querySelector(`label[for="${fieldElement.id}"]`);
    
    if (!hasLabel) {
      issues.push('Field is missing proper label association');
    }

    const hasError = fieldElement.getAttribute('aria-invalid') === 'true';
    const hasErrorDescription = fieldElement.getAttribute('aria-describedby');
    
    if (hasError && !hasErrorDescription) {
      issues.push('Invalid field is missing error description');
    }

    const isRequired = fieldElement.hasAttribute('required') || 
                      fieldElement.getAttribute('aria-required') === 'true';
    
    if (isRequired && fieldElement.getAttribute('aria-required') !== 'true') {
      issues.push('Required field is missing aria-required attribute');
    }

    return issues;
  },
};

/**
 * Accessibility audit utilities
 */
export class AccessibilityAudit {
  static auditPage(): { passed: boolean; issues: string[]; score: number } {
    const issues: string[] = [];
    
    // Check for page title
    if (!document.title || document.title.trim() === '') {
      issues.push('Page is missing a title');
    }

    // Check for main landmark
    const main = document.querySelector('main, [role="main"]');
    if (!main) {
      issues.push('Page is missing a main landmark');
    }

    // Check for heading hierarchy
    const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    if (headings.length === 0) {
      issues.push('Page has no headings');
    } else {
      const h1Count = document.querySelectorAll('h1').length;
      if (h1Count === 0) {
        issues.push('Page is missing an h1 heading');
      } else if (h1Count > 1) {
        issues.push('Page has multiple h1 headings');
      }
    }

    // Check images for alt text
    const images = document.querySelectorAll('img');
    images.forEach((img, index) => {
      if (!img.hasAttribute('alt')) {
        issues.push(`Image ${index + 1} is missing alt attribute`);
      }
    });

    // Check form inputs for labels
    const inputs = document.querySelectorAll('input, select, textarea');
    inputs.forEach((input, index) => {
      const issues_found = FormAccessibility.validateFieldAccessibility(input as HTMLElement);
      issues.push(...issues_found.map(issue => `Input ${index + 1}: ${issue}`));
    });

    const score = Math.max(0, 100 - (issues.length * 10));
    
    return {
      passed: issues.length === 0,
      issues,
      score,
    };
  }

  static logAuditResults(): void {
    const results = this.auditPage();
    
    console.group('🔍 Accessibility Audit Results');
    console.log(`Score: ${results.score}/100`);
    console.log(`Status: ${results.passed ? '✅ PASSED' : '❌ FAILED'}`);
    
    if (results.issues.length > 0) {
      console.group('Issues Found:');
      results.issues.forEach((issue, index) => {
        console.log(`${index + 1}. ${issue}`);
      });
      console.groupEnd();
    }
    
    console.groupEnd();
  }
}

export default {
  FocusManager,
  ColorContrast,
  useKeyboardNavigation,
  useAriaLiveRegion,
  useReducedMotion,
  useHighContrastMode,
  useFocusVisible,
  FormAccessibility,
  AccessibilityAudit,
};